/**
 * Event builder editor modu (musteri sitesi tarafi).
 *
 * `?supsis-editor=NONCE` ile acilan sayfada calisir:
 *  - "Sayfadan sec": overlay ile element secimi, secici adaylari panele gonderilir
 *  - "Sitede test et": panelin gonderdigi (kaydedilmemis) kural seti test modunda calisir,
 *    uretilen event'ler collector'a gitmez, panele geri gonderilir
 *  - JS govdelerinin sayfada denenmesi (editor:eval)
 */
import log2 from "../../../js/log2-lite";
import { readNonce, verifyNonce, editorChannel } from "./session";
import editorOverlay from "./overlay";
import { describeSelection } from "./selectorCandidates";
import rulesEngine from "../rules/engine";
import { runJs } from "../rules/jsRunner";
import pathMatcher from "../rules/paths";

const log = log2("TRACKER_EDITOR");

const MESSAGES = {
    READY: "editor:ready",
    SELECTED: "editor:selected",
    MATCH_COUNT: "editor:match-count",
    TEST_EVENT: "editor:test-event",
    NAVIGATED: "editor:navigated",
    EVAL_RESULT: "editor:eval-result",
    JS_ERROR: "editor:js-error",
};

class Editor {
    constructor() {
        this.nonce = null;
        this.siteId = null;
        this.tracker = null;
        this.started = false;
        this.pickRuleId = null;
    }

    /**
     * @param {Object} tracker SupsisTracker (event'ler icin bagli olmasi gerekmez)
     * @param {String} siteId
     */
    start(tracker, siteId) {
        const nonce = readNonce();
        if (!nonce || this.started) return;

        this.tracker = tracker;
        this.siteId = siteId;
        this.nonce = nonce;

        const self = this;
        verifyNonce(siteId, nonce).then(function (origin) {
            if (!origin) {
                log.debug("editor session rejected");
                return;
            }
            self.open(origin);
        });
    }

    open(origin) {
        const self = this;
        const connected = editorChannel.open(this.nonce, origin, function (message) {
            self.handle(message);
        });
        if (!connected) return;

        this.started = true;
        // Kural motoru editor modunda transport'a yazmaz
        rulesEngine.attach(this.tracker, this.siteId);
        this.bindNavigation();

        editorChannel.send(MESSAGES.READY, {
            pathname: window.location.pathname,
            url: String(window.location.href),
            title: document.title || "",
        });
        log.info("editor mode ready", { origin: origin });
    }

    /** SPA gecislerinde panelin sayfa kapsamini guncelleyebilmesi icin. */
    bindNavigation() {
        const self = this;
        let lastPath = window.location.pathname;
        setInterval(function () {
            if (window.location.pathname === lastPath) return;
            lastPath = window.location.pathname;
            editorChannel.send(MESSAGES.NAVIGATED, {
                pathname: lastPath,
                url: String(window.location.href),
                title: document.title || "",
            });
        }, 1000);
    }

    handle(message) {
        const type = message.type;

        if (type === "editor:pick") return this.startPicking(message.ruleId, message.label);
        if (type === "editor:rules") return this.applyTestRules(message.ruleset);
        if (type === "editor:count") return this.reportCount(message.ruleId, message.sel);
        if (type === "editor:highlight") return this.highlight(message.sel);
        if (type === "editor:eval") return this.evaluate(message);
        if (type === "editor:stop") return this.stopTesting();
    }

    startPicking(ruleId, label) {
        const self = this;
        this.pickRuleId = ruleId || null;

        editorOverlay.start(function (element) {
            const selection = describeSelection(element);
            editorChannel.send(MESSAGES.SELECTED, {
                ruleId: self.pickRuleId,
                selection: selection,
            });
        }, label || "Supsis: bir element secin (gezinmek icin Alt, cikmak icin Esc)");
    }

    /**
     * Panelden gelen kural setini test modunda calistirir.
     * Event'ler transport'a gitmez; panele `editor:test-event` olarak yollanir.
     */
    applyTestRules(ruleset) {
        rulesEngine.setTestHandler(function (event) {
            editorChannel.send(MESSAGES.TEST_EVENT, { event: event });
            if (event.errors && event.errors.length > 0) {
                editorChannel.send(MESSAGES.JS_ERROR, { ruleId: event.ruleId, errors: event.errors });
            }
        });

        const count = rulesEngine.configure(ruleset || { rules: [], paths: [] });
        rulesEngine.start();

        // Panelin eslesme rozetlerini tazelemesi icin her kuralin sayisi gonderilir
        const rules = (ruleset && ruleset.rules) || [];
        for (let i = 0; i < rules.length; i++) {
            if (rules[i] && rules[i].sel) this.reportCount(rules[i].id, rules[i].sel);
        }
        log.debug("test ruleset applied", { count: count });
    }

    stopTesting() {
        rulesEngine.setTestHandler(null);
        editorOverlay.stop();
    }

    reportCount(ruleId, selector) {
        editorChannel.send(MESSAGES.MATCH_COUNT, {
            ruleId: ruleId,
            sel: selector,
            count: rulesEngine.countMatches(selector),
        });
    }

    highlight(selector) {
        const count = editorOverlay.highlightSelector(selector);
        editorChannel.send(MESSAGES.MATCH_COUNT, { sel: selector, count: count });
    }

    /** Panelden gelen JS govdesini sayfada bir kez calistirir ve sonucu doner. */
    evaluate(message) {
        const selector = message.sel;
        let element = null;
        if (selector) {
            try {
                element = document.querySelector(selector);
            } catch (e) {
                element = null;
            }
        }

        const context = {
            el: element,
            event: null,
            page: {
                url: String(window.location.href),
                path: window.location.pathname,
                query: window.location.search,
                title: document.title || "",
                referrer: document.referrer || "",
                pattern: pathMatcher.resolvePattern(),
            },
            visitor: this.tracker && this.tracker.isStarted() ? this.tracker.visitorInfo() : {},
            rule: { id: message.ruleId, name: message.name || "", when: message.when || "" },
            props: message.props || {},
        };

        // Derleme cache'i govdeyi de tuttugu icin kod degisince kendiliginden yenilenir
        const cacheKey = "eval:" + String(message.ruleId) + ":" + String(message.prop);

        runJs(cacheKey, message.body, context).then(function (result) {
            editorChannel.send(MESSAGES.EVAL_RESULT, {
                ruleId: message.ruleId,
                prop: message.prop,
                value: result.ok ? serialize(result.value) : null,
                error: result.error,
            });
        });
    }
}

/** Panele gonderilecek deger: postMessage ile tasinabilir tipe cevrilir. */
function serialize(value) {
    if (value === null || value === undefined) return null;
    const type = typeof value;
    if (type === "string" || type === "number" || type === "boolean") return value;
    try {
        return JSON.stringify(value).slice(0, 1000);
    } catch (e) {
        return String(value).slice(0, 1000);
    }
}

export const editor = new Editor();
export default editor;
export { readNonce };



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/editor/index.js
// module id = null
// module chunks = 